import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useActiveWorld } from '../hooks/useActiveWorld';

const SmartReviewQueue = ({ onClose }) => {
  // 🔒 TEAM SUPPORT: operate against the active workspace owner so team
  // members see the owner's review queue instead of their own (empty) one.
  const { userId } = useActiveWorld();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const functions = getFunctions(undefined, 'europe-west1');

  const loadQueue = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const getQueue = httpsCallable(functions, 'getSmartReviewQueue');
      const result = await getQueue({ userId, limit: 100 });
      if (result.data.success) {
        setItems(result.data.items || []);
      }
    } catch (err) {
      console.error('Error loading review queue:', err);
      setError('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, [functions, userId]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const approveItem = async (itemId, mergeWithId) => {
    setProcessing(prev => ({ ...prev, [itemId]: 'approve' }));
    try {
      const approve = httpsCallable(functions, 'approveSmartReviewItem');
      await approve({ userId, itemId, mergeWithId });
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      console.error('Error approving item:', err);
      setError('Failed to approve contact');
    } finally {
      setProcessing(prev => ({ ...prev, [itemId]: null }));
    }
  };

  const rejectItem = async (itemId) => {
    setProcessing(prev => ({ ...prev, [itemId]: 'reject' }));
    try {
      const reject = httpsCallable(functions, 'rejectSmartReviewItem');
      await reject({ userId, itemId });
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      console.error('Error rejecting item:', err);
      setError('Failed to reject contact');
    } finally {
      setProcessing(prev => ({ ...prev, [itemId]: null }));
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Import all ${items.length} contacts into your database?`)) return;
    setBulkProcessing(true);
    try {
      const approveAllFn = httpsCallable(functions, 'approveAllSmartReview');
      const result = await approveAllFn({ userId });
      if (result.data.success) {
        setItems([]);
      }
    } catch (err) {
      console.error('Error approving all:', err);
      setError('Failed to approve all contacts');
    } finally {
      setBulkProcessing(false);
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      ambiguous_company: 'Ambiguous company match',
      low_confidence: 'Low match confidence',
      rule_flag: 'Flagged by rule',
      name_ambiguous: 'Multiple name matches',
    };
    return labels[reason] || reason || 'Unknown';
  };

  const getReasonColor = (reason) => {
    if (reason === 'rule_flag') return '#e67e22';
    if (reason === 'ambiguous_company' || reason === 'name_ambiguous') return '#e74c3c';
    return '#f39c12';
  };

  return (
    <div className="smart-review-overlay" onClick={onClose}>
      <div className="smart-review-modal" onClick={e => e.stopPropagation()}>
        <div className="smart-review-header">
          <div className="smart-review-header-left">
            <h2>Smart Review Queue</h2>
            <span className="smart-review-count">{items.length} contact{items.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="smart-review-header-actions">
            {items.length > 0 && (
              <button
                className="smart-review-approve-all"
                onClick={approveAll}
                disabled={bulkProcessing}
              >
                {bulkProcessing ? 'Importing...' : `Import All (${items.length})`}
              </button>
            )}
            <button className="smart-review-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {error && (
          <div className="smart-review-error">{error}</div>
        )}

        <div className="smart-review-body">
          {loading ? (
            <div className="smart-review-loading">
              <div className="api-settings-spinner" />
              <p>Loading review queue...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="smart-review-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <h3>All Clear</h3>
              <p>No contacts waiting for review. Everything was auto-imported.</p>
            </div>
          ) : (
            <div className="smart-review-list">
              {items.map(item => (
                <div key={item.id} className={`smart-review-item ${expandedId === item.id ? 'expanded' : ''}`}>
                  <div
                    className="smart-review-item-header"
                    onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  >
                    <div className="smart-review-item-info">
                      <span className="smart-review-item-name">
                        {item.business_name || item.lead_email || 'Unknown Contact'}
                      </span>
                      {item.lead_email && item.business_name && (
                        <span className="smart-review-item-email">{item.lead_email}</span>
                      )}
                    </div>
                    <div className="smart-review-item-meta">
                      <span
                        className="smart-review-reason-badge"
                        style={{ borderColor: getReasonColor(item._reviewReason) }}
                      >
                        {getReasonLabel(item._reviewReason)}
                      </span>
                    </div>
                    <div className="smart-review-item-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="smart-review-btn approve"
                        onClick={() => approveItem(item.id)}
                        disabled={!!processing[item.id]}
                        title="Import"
                      >
                        {processing[item.id] === 'approve' ? '...' : '✓'}
                      </button>
                      <button
                        className="smart-review-btn reject"
                        onClick={() => rejectItem(item.id)}
                        disabled={!!processing[item.id]}
                        title="Reject"
                      >
                        {processing[item.id] === 'reject' ? '...' : '✗'}
                      </button>
                    </div>
                  </div>

                  {expandedId === item.id && (
                    <div className="smart-review-item-details">
                      <div className="smart-review-detail-grid">
                        {item.business_name && (
                          <div className="smart-review-detail">
                            <label>Company</label>
                            <span>{item.business_name}</span>
                          </div>
                        )}
                        {item.lead_email && (
                          <div className="smart-review-detail">
                            <label>Email</label>
                            <span>{item.lead_email}</span>
                          </div>
                        )}
                        {item.lead_telephone && (
                          <div className="smart-review-detail">
                            <label>Phone</label>
                            <span>{item.lead_telephone}</span>
                          </div>
                        )}
                        {item.lead_website && (
                          <div className="smart-review-detail">
                            <label>Website</label>
                            <span>{item.lead_website}</span>
                          </div>
                        )}
                        {item.lead_city && (
                          <div className="smart-review-detail">
                            <label>City</label>
                            <span>{item.lead_city}</span>
                          </div>
                        )}
                        {item.lead_description && (
                          <div className="smart-review-detail full">
                            <label>Description</label>
                            <span>{item.lead_description.substring(0, 200)}</span>
                          </div>
                        )}
                      </div>

                      {item._companyResolution && (
                        <div className="smart-review-resolution">
                          <h4>Company Match</h4>
                          <div className="smart-review-resolution-info">
                            {item._companyResolution.match_reason && (
                              <span className="resolution-tag">
                                {item._companyResolution.match_reason}
                              </span>
                            )}
                            {item._companyResolution.match_confidence > 0 && (
                              <span className="resolution-confidence">
                                {Math.round(item._companyResolution.match_confidence * 100)}% confidence
                              </span>
                            )}
                          </div>
                          {item._companyResolution.resolved_company_id && (
                            <button
                              className="smart-review-merge-btn"
                              onClick={() => approveItem(item.id, item._companyResolution.resolved_company_id)}
                              disabled={!!processing[item.id]}
                            >
                              Merge with existing contact
                            </button>
                          )}
                        </div>
                      )}

                      <div className="smart-review-item-full-actions">
                        <button
                          className="smart-review-action-btn primary"
                          onClick={() => approveItem(item.id)}
                          disabled={!!processing[item.id]}
                        >
                          {processing[item.id] === 'approve' ? 'Importing...' : 'Import as New Contact'}
                        </button>
                        <button
                          className="smart-review-action-btn danger"
                          onClick={() => rejectItem(item.id)}
                          disabled={!!processing[item.id]}
                        >
                          {processing[item.id] === 'reject' ? 'Rejecting...' : 'Dismiss'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartReviewQueue;
